import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSchoolAdminLoginMutation } from '../../store/api/authApi';
import { setCredentials } from '../../store/authSlice';
import { useAppDispatch } from '../../store/hooks';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import alforaaFullLogoDark from '../../assets/svg/logo/alforaa_full_logo_dark.svg';
import { ButtonSpinner } from '../../components/shared/SharedComponents';
import { analytics, identifyUser } from '../../services/analytics';
import { usePageView } from '../../hooks/usePageView';
import { EVENTS } from '../../constants/analyticsEvents';

export default function SchoolAdminLoginPage() {
	usePageView(EVENTS.schoolAdmin.LOGIN_PAGE_VIEWED);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [loginMut] = useSchoolAdminLoginMutation();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	const canSubmit = emailValid && password.length > 0 && !loading;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		if (!canSubmit) return;
		setLoading(true);
		analytics.schoolAdmin.loginSubmitted();
		try {
			const res = await loginMut({
				email: email.trim(),
				password,
			}).unwrap();
			identifyUser(res.user, 'school_admin');
			analytics.schoolAdmin.loginSucceeded();
			dispatch(
				setCredentials({
					user: res.user,
					role: 'school_admin',
					accessToken: res.accessToken,
					refreshToken: res.refreshToken,
					mustChangePassword: res.mustChangePassword,
				}),
			);
			if (res.mustChangePassword) {
				navigate('/school/setup');
			} else {
				navigate('/school/dashboard');
			}
		} catch (e: any) {
			const rawReason = (e as any)?.data?.detail || '';
			setError('Email or password is incorrect');
			analytics.schoolAdmin.loginFailed(rawReason || 'invalid_credentials');
		}
		setLoading(false);
	};

	return (
		<div
			className='min-h-screen aurora-bg flex flex-col lg:flex-row relative overflow-hidden'
			data-testid='school-admin-login-page'>
			<div
				aria-hidden
				className='pointer-events-none absolute top-[-100px] left-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(255,180,140,0.55), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>
			<div
				aria-hidden
				className='pointer-events-none absolute bottom-[-100px] right-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(220,200,255,0.6), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>

			{/* Mobile/tablet: compact logo header */}
			<div className='lg:hidden flex items-center justify-center pt-8 pb-2 px-6 relative z-10'>
				<img
					src={alforaaFullLogoDark}
					alt='Alforaa'
					className='h-9 w-auto block'
				/>
			</div>

			{/* Left visual pane */}
			<div className='hidden lg:flex flex-1 flex-col justify-between p-14 relative z-10'>
				<div className='flex items-center'>
					<img
						src={alforaaFullLogoDark}
						alt='Alforaa'
						className='h-10 w-auto block'
					/>
				</div>
				<div>
					<div className='text-[14px] text-aurora-coral font-semibold uppercase tracking-[0.04em]'>
						For school admins
					</div>
					<div className='text-[56px] font-semibold tracking-[-0.04em] text-aurora-ink leading-[1.05] mt-3'>
						Run your school,<br />
						<span
							style={{
								background: 'linear-gradient(135deg, #FF7A45, #E8541F)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}>
							end to end.
						</span>
					</div>
					<div className='text-[16px] text-aurora-ink-soft leading-relaxed mt-4 max-w-[460px]'>
						Manage classes, trainers, and students from one place. Track
						curriculum progress and outcomes across every grade.
					</div>
				</div>
				<div className='flex gap-5 items-center'>
					<div className='flex'>
						{[
							'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=160&h=160&fit=crop&crop=faces&q=80',
						].map((src, i) => (
							<img
								key={i}
								src={src}
								alt=''
								className='w-9 h-9 rounded-full border-2 border-white object-cover bg-aurora-coral-soft'
								style={{ marginLeft: i === 0 ? 0 : -10 }}
							/>
						))}
					</div>
					<div className='text-[12px] text-aurora-ink-soft'>
						<div className='font-semibold text-aurora-ink'>
							Built for school leaders
						</div>
						<div>One dashboard, full visibility.</div>
					</div>
				</div>
			</div>

			{/* Right login card */}
			<div className='flex-1 flex items-center justify-center relative z-10 p-6 sm:p-10 lg:p-0'>
				<form
					onSubmit={handleSubmit}
					className='aurora-glass-strong rounded-[28px] p-7 sm:p-10 w-full max-w-[440px] animate-fade-in'>
					<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
						Welcome back
					</div>
					<div className='text-[13px] text-aurora-ink-mute mt-1'>
						Sign in to your school admin dashboard
					</div>

					<div className='mt-6'>
						<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
							Email
						</label>
						<div className='relative'>
							<Mail
								size={16}
								className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
							/>
							<input
								type='email'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder='admin@school.ae'
								data-testid='school-email-input'
								className='w-full pl-10 pr-4 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
							/>
						</div>
					</div>

					<div className='mt-4'>
						<div className='mb-1.5'>
							<label className='text-[11.5px] font-medium text-aurora-ink-soft'>
								Password
							</label>
						</div>
						<div className='relative'>
							<Lock
								size={16}
								className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
							/>
							<input
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder='Enter your password'
								data-testid='school-password-input'
								className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
							/>
							<button
								type='button'
								onClick={() => setShowPassword((v) => !v)}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
								{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
							</button>
						</div>
					</div>

					{error && (
						<p
							className='text-status-error text-[12px] text-center mt-4'
							data-testid='school-login-error'>
							{error}
						</p>
					)}

					<button
						type='submit'
						disabled={!canSubmit}
						data-testid='school-login-submit'
						className='w-full py-3 mt-5 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2'>
						{loading && <ButtonSpinner />}
						{loading ? 'Signing in…' : 'Sign in'}
						{!loading && <ArrowRight size={16} />}
					</button>
				</form>
			</div>
		</div>
	);
}
